import axios from 'axios'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useApolloClient, useMutation} from '@apollo/client'

import {CREATE_TRANSCRIBE_PRESIGNER, CREATE_UPLOAD, CREATE_UPLOAD_PRESIGNER} from '../../../../../../graphql/mutations/userData'
import {UPLOAD} from '../../../../../../graphql/queries/userData'
import {createDictation, speechSupported} from '../lib/speech'
import {createTranscribeDictation, transcribeStreamingSupported} from '../lib/transcribeStream'
import {recordingFilename} from '../lifeStoryTools'
import {touchSessionActivity} from '../../../../../../lib/sessionActivity'

export const RECORDER_STATES = {
  IDLE: 'idle',
  RECORDING: 'recording',
  PAUSED: 'paused',
  PROCESSING: 'processing'
}

// Recording or paused: the mic is held and the session is still open.
export const isLiveRecorderState = state => state === RECORDER_STATES.RECORDING || state === RECORDER_STATES.PAUSED

const PREFERRED_MIME_TYPES = [
  {mimeType: 'audio/mp4', extension: 'm4a'},
  {mimeType: 'audio/webm', extension: 'webm'}
]

// Recordings are capped at 10 minutes (TK-3017); the warning gives the
// speaker a minute to wrap up before the recorder stops for them.
export const MAX_RECORDING_SECONDS = 600
export const CAP_WARNING_SECONDS = 540

// iOS Safari can hand the page a muted audio track right after the
// first-ever microphone permission grant, while the system audio session is
// still activating. MediaRecorder captures nothing from a muted track — the
// timer keeps counting, so without a guard the user records minutes of
// nothing and saves an unplayable 0-byte file. The grace period lets the
// track finish activating (brief mutes are normal) before giving up.
export const MUTED_MIC_GRACE_MS = 2000

const NO_AUDIO_CAPTURED_MESSAGE = 'We couldn\u2019t capture any audio from your microphone. Please try recording again.'

const supportedRecordingFormat = () => {
  if (typeof MediaRecorder === 'undefined') return null

  return PREFERRED_MIME_TYPES.find(format => MediaRecorder.isTypeSupported(format.mimeType)) || {}
}

const ENCRYPTION_POLL_INTERVAL = 1500
const ENCRYPTION_POLL_LIMIT = 40

/**
 * Records the user's voice for one Life Story answer.
 *
 * While recording, Amazon Transcribe streaming (Web Speech API as fallback)
 * transcribes speech live into the answer (via `onTranscript`), with
 * punctuation and capitalization. When the user stops, the audio is uploaded directly to
 * S3 through a presigned URL and registered as an encrypted `Upload`, and
 * `onRecordingComplete` receives {'upload-id', duration, filename} to be saved
 * with the story so it can be replayed and published to the vault.
 *
 * Pause/Resume (TK-3204): the recorder and the dictation hold, the timer
 * holds (the cap counts recorded seconds only), and Stop from paused still
 * saves what was recorded. `onPauseChange(paused)` lets the screen announce it.
 *
 * `transcribe: false` records without dictation ("Record this version": the
 * story is already written, the recording is the reading of it).
 */
const useVoiceRecorder = ({userConfig, question, onTranscript, onRecordingComplete, onError, onCapReached, onPauseChange, transcribe = true}) => {
  const apolloClient = useApolloClient()
  const [createTranscribePresigner] = useMutation(CREATE_TRANSCRIBE_PRESIGNER)
  const [createUploadPresigner] = useMutation(CREATE_UPLOAD_PRESIGNER)
  const [createUpload] = useMutation(CREATE_UPLOAD)

  const [recorderState, setRecorderState] = useState(RECORDER_STATES.IDLE)
  // True from Start until the microphone is granted (or refused). The state
  // stays IDLE meanwhile, so callers that must not let the surface close (or
  // a second recorder start) with a permission prompt pending read this.
  const [starting, setStarting] = useState(false)
  // Identity of the start in flight; a cancelled or superseded start finds
  // it changed and lets the stream it was granted go.
  const startTokenRef = useRef(null)
  const [duration, setDuration] = useState(0)

  const mediaRecorderRef = useRef(null)
  const streamRef = useRef(null)
  const dictationRef = useRef(null)
  const chunksRef = useRef([])
  const durationRef = useRef(0)
  const timerRef = useRef(null)
  const muteTimerRef = useRef(null)
  const formatRef = useRef(null)
  // Read by the timer, the mute watchdog and the dictation fallback, none of
  // which re-render with state.
  const pausedRef = useRef(false)
  // The duration timer must call the latest stopRecording (it's rebuilt when
  // recorderState changes) and onCapReached (callers may pass a fresh
  // function every render), but the interval closure only ever sees one
  // version — so the cap goes through refs.
  const stopRecordingRef = useRef(() => {})
  const onCapReachedRef = useRef(onCapReached)

  onCapReachedRef.current = onCapReached

  const cleanupMedia = () => {
    clearInterval(timerRef.current)
    clearTimeout(muteTimerRef.current)
    pausedRef.current = false

    if (dictationRef.current) {
      dictationRef.current.stop()
      dictationRef.current = null
    }

    // Unmounting mid-recording (route change, reload) must stop the recorder
    // too, or the mic stays hot. Handlers are detached first so this teardown
    // path never triggers the upload flow.
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null
      mediaRecorderRef.current.onstop = null

      if (mediaRecorderRef.current.state !== 'inactive')
        mediaRecorderRef.current.stop()

      mediaRecorderRef.current = null
    }

    // Track handlers are detached like the recorder's above: a stale track
    // firing mute/unmute after cleanup must not clear or replace the mute
    // watchdog timer of a session that started later.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.onended = null
        track.onmute = null
        track.onunmute = null
        track.stop()
      })
      streamRef.current = null
    }
  }

  useEffect(() => () => {
    startTokenRef.current = null
    cleanupMedia()
  }, [])

  const pollUntilEncrypted = uploadId => new Promise((resolve, reject) => {
    let attempts = 0

    const poll = () => {
      attempts += 1

      apolloClient.query({query: UPLOAD, variables: {id: uploadId}, fetchPolicy: 'network-only'})
        .then(({data}) => {
          const status = data.upload.status

          if (status === 'encrypted') return resolve(data.upload)
          if (status === 'virus_detected') return reject(new Error('virus_detected'))
          if (attempts >= ENCRYPTION_POLL_LIMIT) return resolve(data.upload) // still processing; the upload is registered

          return setTimeout(poll, ENCRYPTION_POLL_INTERVAL)
        })
        .catch(reject)
    }

    poll()
  })

  const uploadRecording = blob => {
    const filename = recordingFilename({question, extension: formatRef.current.extension})

    return createUploadPresigner({variables: {filename}})
      .then(({data}) => {
        const presigner = data.createUploadPresigner

        return axios.put(presigner.presignedUrl, blob, {headers: {'Content-Type': blob.type}})
          .then(() => createUpload({
            variables: {
              input: {
                filename: presigner.filename,
                filesize: blob.size,
                everplanId: userConfig.get('everplan-id'),
                originalFileKey: presigner.fileObjectKey,
                filetype: blob.type
              }
            }
          }))
      })
      .then(({data}) => pollUntilEncrypted(data.createUpload.id).then(() => data.createUpload))
  }

  const startWebSpeechDictation = () => {
    if (!speechSupported()) return

    dictationRef.current = createDictation({
      onResult: (finalText, interimText) => onTranscript(finalText, interimText),
      onEnd: () => {}
    })
    // A fallback that lands while paused waits for Resume, so nothing said
    // while paused is written down.
    if (!pausedRef.current) dictationRef.current.start()
  }

  /**
   * Live transcription prefers Amazon Transcribe streaming (punctuated,
   * capitalized, multi-language) and falls back to the browser's Web Speech
   * API when the presigner or socket can't be set up — or when the stream
   * dies mid-recording, so words keep landing either way. The recording
   * itself (MediaRecorder -> S3) is unaffected by any of this.
   */
  const startDictation = stream => {
    if (!transcribeStreamingSupported()) {
      startWebSpeechDictation()

      return
    }

    // A failed session can surface through both onEnd and the promise
    // rejection. Falling back twice would start two Web Speech recognizers
    // that abort each other while recording, and orphan one of them (never
    // stopped, transcribing past the end of the recording) — so exactly one
    // fallback may run per recording session.
    let fellBack = false

    const fallBackToWebSpeech = (warning, error) => {
      if (fellBack || streamRef.current !== stream) return

      fellBack = true
      // eslint-disable-next-line no-console
      console.warn(warning, ...(error ? [error] : []))
      startWebSpeechDictation()
    }

    createTranscribePresigner({variables: {languageCode: navigator.language}})
      .then(({data}) => createTranscribeDictation({
        stream,
        presigner: data.createTranscribePresigner,
        onResult: onTranscript,
        // Applies the pause from the first frame: a session that opens while
        // paused must not forward the audio between opening and pause().
        isPaused: () => pausedRef.current,
        // onEnd also fires after our own stop() — cleanupMedia clears
        // streamRef first, so the guard keeps intentional stops silent.
        onEnd: () => {
          // Once fallen back, dictationRef holds the Web Speech dictation;
          // nulling it here would orphan a live recognizer.
          if (!fellBack && streamRef.current === stream) dictationRef.current = null

          fallBackToWebSpeech('Transcribe stream ended mid-recording; falling back to Web Speech dictation.')
        }
      }))
      .then(dictation => {
        // The user may have stopped recording (or the session may have
        // already fallen back) while the socket was opening.
        if (streamRef.current === stream && !fellBack) {
          dictationRef.current = dictation
          if (pausedRef.current) dictation.pause()
        } else {
          dictation.stop()
        }
      })
      .catch(error => {
        fallBackToWebSpeech('Transcribe dictation unavailable; falling back to Web Speech dictation.', error)
      })
  }

  const startRecording = useCallback(() => {
    const format = supportedRecordingFormat()

    // `mediaDevices` can exist without `getUserMedia` (insecure contexts, older
    // browsers); calling it anyway would throw synchronously past the .catch.
    if (!format || !format.mimeType || typeof navigator.mediaDevices?.getUserMedia !== 'function') {
      onError('Recording isn\u2019t supported in this browser. You can still write your answer.')

      return
    }

    // One pending start at a time: a second press while the permission
    // prompt is up would ask again and open a second stream.
    if (startTokenRef.current) return

    const token = {}

    startTokenRef.current = token
    setStarting(true)

    navigator.mediaDevices.getUserMedia({audio: true})
      .then(stream => {
        // Cancelled (the surface closed, the hook unmounted) while the
        // permission prompt was up: nothing may start now.
        if (startTokenRef.current !== token) {
          stream.getTracks().forEach(track => track.stop())

          return
        }

        // The token stays valid through setup so a failure below (an
        // unsupported MediaRecorder option, a dictation error) is reported
        // rather than mistaken for a cancelled start.
        formatRef.current = format
        streamRef.current = stream
        chunksRef.current = []
        durationRef.current = 0
        setDuration(0)

        const mediaRecorder = new MediaRecorder(stream, {mimeType: format.mimeType})

        mediaRecorder.ondataavailable = event => {
          if (event.data && event.data.size > 0)
            chunksRef.current.push(event.data)
        }

        // The recorder can stop without the user asking (mic permission
        // revoked, device disconnected, track ended). Without these defaults
        // the hook would sit in RECORDING forever with the timer and dictation
        // still running. stopRecording() overrides onstop for the normal flow.
        const abortWithError = message => {
          cleanupMedia()
          setRecorderState(RECORDER_STATES.IDLE)
          onError(message)
        }

        const abortRecording = () =>
          abortWithError('The recording stopped unexpectedly. Everything transcribed so far is still in the text below.')

        // `onstop === abortRecording` means the session is still live —
        // stopRecording() replaces onstop for the normal flow, and
        // cleanupMedia detaches it entirely.
        const sessionIsLive = () =>
          mediaRecorderRef.current === mediaRecorder && mediaRecorder.onstop === abortRecording

        // Not while paused: a paused recorder captures nothing anyway, and a
        // track that mutes while paused is checked again on Resume.
        const startMuteWatchdog = () => {
          clearTimeout(muteTimerRef.current)
          if (pausedRef.current) return

          muteTimerRef.current = setTimeout(() => {
            if (sessionIsLive()) abortWithError(NO_AUDIO_CAPTURED_MESSAGE)
          }, MUTED_MIC_GRACE_MS)
        }

        mediaRecorder.onresume = () => {
          if (sessionIsLive() && stream.getTracks().some(track => track.muted)) startMuteWatchdog()
        }

        mediaRecorder.onstop = abortRecording
        mediaRecorder.onerror = abortRecording
        stream.getTracks().forEach(track => {
          track.onended = () => {
            if (sessionIsLive()) abortRecording()
          }
          track.onmute = startMuteWatchdog
          track.onunmute = () => clearTimeout(muteTimerRef.current)
        })

        // The track may already be muted at start (see MUTED_MIC_GRACE_MS).
        if (stream.getTracks().some(track => track.muted)) startMuteWatchdog()

        mediaRecorder.start()
        mediaRecorderRef.current = mediaRecorder

        timerRef.current = setInterval(() => {
          // Someone speaking into the mic produces no DOM events, so each
          // tick marks them active for the session inactivity timeout — a
          // long recording shouldn't end in a timeout warning (AC 10.1). A
          // paused recording is still an open task, so the tick keeps going
          // while the duration holds.
          touchSessionActivity()

          if (pausedRef.current) return

          durationRef.current += 1
          setDuration(durationRef.current)

          // Stop for the user at the cap; the recording so far uploads and
          // transcribes through the normal stop flow, nothing is discarded.
          // The recorder-state check keeps a cap tick that lands just after a
          // manual stop (the interval runs until onstop fires) from toasting
          // and stopping a second time.
          if (durationRef.current === MAX_RECORDING_SECONDS && mediaRecorder.state === 'recording') {
            if (onCapReachedRef.current) onCapReachedRef.current()

            stopRecordingRef.current()
          }
        }, 1000)

        if (transcribe) startDictation(stream)
        startTokenRef.current = null
        setStarting(false)
        setRecorderState(RECORDER_STATES.RECORDING)
      })
      .catch(() => {
        // Cancelled while the prompt was up: the granted stream (if any) was
        // released above and there is nothing to report.
        if (startTokenRef.current !== token) return

        // Permission refused, or setup threw after the grant: either way the
        // mic must not stay live behind a failed start.
        cleanupMedia()
        startTokenRef.current = null
        setStarting(false)
        onError('We couldn\u2019t access your microphone. Check your browser permissions and try again.')
      })
  }, [onTranscript, onError, transcribe])

  /** Forgets a Start still waiting on the microphone: when it is granted, the stream is released unused. */
  const cancelStart = useCallback(() => {
    startTokenRef.current = null
    setStarting(false)
  }, [])

  const pauseRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current

    // The native recorder can already be inactive (an unexpected stop
    // whose onstop hasn't updated React state yet); pausing then would show
    // a Paused UI over a recorder that can never resume, so leave it to the
    // stop path.
    if (!mediaRecorder || recorderState !== RECORDER_STATES.RECORDING || mediaRecorder.state !== 'recording') return

    pausedRef.current = true
    clearTimeout(muteTimerRef.current)
    mediaRecorder.pause()
    if (dictationRef.current) dictationRef.current.pause()
    setRecorderState(RECORDER_STATES.PAUSED)
    if (onPauseChange) onPauseChange(true)
  }, [recorderState, onPauseChange])

  const resumeRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current

    if (!mediaRecorder || recorderState !== RECORDER_STATES.PAUSED || mediaRecorder.state !== 'paused') return

    pausedRef.current = false
    mediaRecorder.resume()
    if (dictationRef.current) dictationRef.current.resume()
    setRecorderState(RECORDER_STATES.RECORDING)
    if (onPauseChange) onPauseChange(false)
  }, [recorderState, onPauseChange])

  const stopRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current

    if (!mediaRecorder || !isLiveRecorderState(recorderState))
      return

    setRecorderState(RECORDER_STATES.PROCESSING)

    const recordingDuration = durationRef.current

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, {type: formatRef.current.mimeType})

      cleanupMedia()

      // A muted or dead mic (seen on iOS Safari right after the first-ever
      // permission grant) produces no chunks at all. Uploading anyway would
      // put an unplayable 0-byte file in the vault, labeled with a
      // plausible-looking duration from the timer.
      if (blob.size === 0) {
        onError(NO_AUDIO_CAPTURED_MESSAGE)
        setRecorderState(RECORDER_STATES.IDLE)

        return
      }

      uploadRecording(blob)
        .then(upload => {
          onRecordingComplete({
            'upload-id': upload.id,
            duration: recordingDuration,
            filename: upload.filename
          })
          setRecorderState(RECORDER_STATES.IDLE)
        })
        .catch(() => {
          onError('We couldn\u2019t save your recording, but every word you spoke is still in the text below.')
          setRecorderState(RECORDER_STATES.IDLE)
        })
    }

    mediaRecorder.stop()
  }, [recorderState, onRecordingComplete, onError, question])

  stopRecordingRef.current = stopRecording

  return {
    recorderState,
    duration,
    starting,
    startRecording,
    cancelStart,
    pauseRecording,
    resumeRecording,
    stopRecording,
    nearingCap: isLiveRecorderState(recorderState) && duration >= CAP_WARNING_SECONDS,
    recordingSupported: Boolean(supportedRecordingFormat()?.mimeType)
  }
}

export default useVoiceRecorder
